import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { User } from '@supabase/supabase-js';
import { fetchApi } from '../../lib/apiClient';

/**
 * 邀请码验证状态缓存数据结构
 */
interface InviteCodeVerificationCache {
  needsVerification: boolean;
  timestamp: number;
  userId: string;
}

/**
 * 邀请码验证状态缓存 atom
 * 使用 localStorage 持久化存储，缓存有效期1天
 */
export const inviteCodeVerificationCacheAtom = atomWithStorage<InviteCodeVerificationCache | null>(
  'v3_inviteCodeVerificationStatus',
  null
);

/**
 * 缓存有效期：1天（毫秒）
 */
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 86400000ms

/**
 * 检查缓存是否有效
 */
const isCacheValid = (cache: InviteCodeVerificationCache | null, userId: string): boolean => {
  if (!cache) return false;
  if (cache.userId !== userId) return false;
  if (Date.now() - cache.timestamp > CACHE_EXPIRY_MS) return false;
  return true;
};

/**
 * 邀请码验证状态检查 atom
 * 包含缓存逻辑的异步检查
 */
export const checkInviteCodeVerificationAtom = atom(
  null,
  async (get, set, user: User | null) => {
    if (!user) return false;

    const cache = get(inviteCodeVerificationCacheAtom);
    
    // 检查缓存是否有效
    if (isCacheValid(cache, user.id)) {
      console.log('[InviteCodeAtoms] Using cached verification status:', cache.needsVerification);
      return cache.needsVerification;
    }

    try {
      console.log('[InviteCodeAtoms] Cache invalid or expired, fetching from API');
      
      // 调用 API 获取最新状态
      const data = await fetchApi<{ needsVerification: boolean; message: string }>('/api/invite/status');
      
      const needsVerification = data.needsVerification || false;
      
      // 更新缓存
      const newCache: InviteCodeVerificationCache = {
        needsVerification,
        timestamp: Date.now(),
        userId: user.id,
      };
      
      set(inviteCodeVerificationCacheAtom, newCache);
      
      console.log('[InviteCodeAtoms] Updated cache with new verification status:', needsVerification);
      return needsVerification;
    } catch (error) {
      console.error('[InviteCodeAtoms] Error checking invite code verification need:', error);
      // 出错时默认不需要验证，避免阻止老用户使用应用
      return false;
    }
  }
);

/**
 * 邀请码验证 atom
 * 验证成功后自动清除缓存
 */
export const verifyInviteCodeAtom = atom(
  null,
  async (get, set, code: string) => {
    try {
      if (!code || typeof code !== 'string') {
        return {
          success: false,
          message: '邀请码不能为空'
        };
      }

      // 简单的格式验证
      const codePattern = /^[A-Z0-9-]+$/;
      if (!codePattern.test(code.toUpperCase())) {
        return {
          success: false,
          message: '邀请码格式无效'
        };
      }

      const data = await fetchApi<{ success: boolean; message: string; valid?: boolean }>('/api/invite/verify', {
        method: 'POST',
        body: { code: code.toUpperCase() },
      });

      const result = {
        success: data.success || false,
        message: data.message || '验证成功'
      };

      // 如果验证成功，清除缓存
      if (result.success) {
        console.log('[InviteCodeAtoms] Invite code verification successful, clearing cache');
        set(inviteCodeVerificationCacheAtom, null);
      }

      return result;
    } catch (error) {
      console.error('[InviteCodeAtoms] Error verifying invite code:', error);
      return {
        success: false,
        message: '网络错误，请重试'
      };
    }
  }
);

/**
 * 清除邀请码验证缓存 atom
 * 用于用户登出或邀请码验证成功后清理缓存
 */
export const clearInviteCodeCacheAtom = atom(
  null,
  (get, set) => {
    console.log('[InviteCodeAtoms] Clearing invite code verification cache');
    set(inviteCodeVerificationCacheAtom, null);
  }
);
